// pages/About.js — mobile responsive

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CtaBand from '../components/CtaBand';

const values = [
  { icon: '🏆', title: 'Uncompromising Quality',  text: "We source only the finest materials and work with master craftsmen to ensure every detail meets our exacting standards." },
  { icon: '👥', title: 'Client-Centric Approach', text: "Your vision is our blueprint. We listen, collaborate, and refine until your dream kitchen becomes reality." },
  { icon: '♡',  title: 'Passion for Design',      text: "Design isn't just what we do — it's who we are. We approach every project with creativity and enthusiasm." },
];

export default function About() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div className="page-wrapper--inner">
      <div style={{ height: "60px" }} />
      <style>{`
        .val-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }
        .team-imgs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          align-items: end;
        }
        .stat-pair {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-top: 2.5rem;
        }
        .about-badge {
          position: absolute;
          bottom: -1.5rem; right: -1.5rem;
          background: var(--orange); color: #000;
          padding: 1.5rem 2rem; text-align: center;
        }
        @media (max-width: 768px) {
          .val-grid { grid-template-columns: 1fr; }
          .team-imgs { grid-template-columns: 1fr; }
          .about-badge { bottom: -1rem; right: 1rem; }
        }
        @media (max-width: 480px) {
          .stat-pair { grid-template-columns: 1fr 1fr; gap: 1rem; }
        }
      `}</style>

      {/* ── ABOUT INTRO ── */}
      <section className="section">
        <div className="container">
          <div className="two-col">
            <div>
              <div className="label">About Us</div>
              <h2 className="heading-xl">Designed in Belfast,<br /><span className="orange-italic">Built for Life</span></h2>
              <br />
              <p className="body-p">Urban Kitchens was founded on a simple principle: that every home deserves a kitchen that's not just functional, but extraordinary. Based in Belfast, we've spent over 30 years perfecting the art of bespoke kitchen design.</p>
              <p className="body-p">Our team combines European craftsmanship traditions with contemporary Northern Irish sensibility, creating spaces that are uniquely tailored to each client's lifestyle and aspirations.</p>
            </div>
            <div style={{ position: 'relative', paddingBottom: '1.5rem' }}>
              <img src="/images/belfast-city.jpg" alt="Belfast city" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
              <div className="about-badge">
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '3rem', fontWeight: 600, lineHeight: 1 }}>25+</div>
                <div style={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Years of Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="section section--dark">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div className="label" style={{ justifyContent: 'center' }}>Our Values</div>
            <h2 style={s.sectionH}>What Drives Us</h2>
          </div>
          <div className="val-grid">
            {values.map((v, i) => (
              <div key={i} style={s.valCard}>
                <div style={s.valIcon}>{v.icon}</div>
                <h3 style={s.valTitle}>{v.title}</h3>
                <p className="body-p" style={{ marginBottom: 0 }}>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="section">
        <div className="container">
          <div className="two-col">
            <div className="team-imgs">
               <img src="/images/fitter.jpg" alt="Kitchen fitter" style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }} />
               <img src="/images/cooking-at-home.jpg" alt="Cooking at home" style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block', marginTop: '3rem' }} />
            </div>
            <div>
              <div className="label">Our Team</div>
              <h2 className="heading-xl">Experts in<br />Every Detail</h2>
              <br />
              <p className="subtext">Our team brings together decades of combined experience in kitchen design, architecture, and craftsmanship. Every member shares a commitment to excellence.</p>
              <br />
              <p className="subtext">We invest continuously in training and development, ensuring our team stays at the forefront of design trends and sustainable practices.</p>
              <div className="stat-pair">
                <div><div style={s.statNum}>200+</div><div style={s.statLabel}>Projects Completed</div></div>
                <div><div style={s.statNum}>99%</div><div style={s.statLabel}>Client Satisfaction</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BELFAST PROUD ── */}
      <section className="section section--dark">
        <div className="container">
          <div className="two-col">
            <div>
              <div className="label">📍 Belfast Proud</div>
              <h2 className="heading-xl">Rooted in<br />Northern Ireland</h2>
              <br />
              <p className="subtext">Belfast has a rich heritage of craftsmanship and innovation. As a Belfast-based company, we're proud to contribute to this tradition, bringing world-class kitchen design to homes across Northern Ireland.</p>
              <br />
              <p className="subtext">Our showroom on the Upper Lisburn Road is a destination for design inspiration. We invite you to visit, experience our work first-hand, and discover the Urban Kitchens difference.</p>
              <button style={s.linkArr} onClick={() => navigate('/contact')}>Visit Our Showroom →</button>
            </div>
            <img src="/images/northern-ireland.jpg" alt="Northern Ireland" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to Start Your Journey?"
        text="Let's discuss your vision and explore how we can create your perfect kitchen."
        buttons={[{ label: 'Book a Consultation', to: '/contact' }]}
      />
    </div>
  );
}

const s = {
  sectionH: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400 },
  valCard: { background: 'var(--bg3)', border: '1px solid var(--border)', padding: '2.5rem' },
  valIcon: { fontSize: '1.8rem', color: 'var(--orange)', marginBottom: '1.5rem' },
  valTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' },
  statNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: '3rem', color: 'var(--orange)', lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: 'var(--text-muted)', letterSpacing: '0.1em', marginTop: '0.3rem' },
  linkArr: { background: 'none', border: 'none', borderBottom: '1px solid var(--orange)', color: 'var(--orange)', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '2rem', padding: '0 0 0.2rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' },
};
